// ReportGeneratorWizard — 6-step wizard to generate a report
import { useState } from "react";
import { generatorReportTypes, contentSections, outputFormats } from "@/mocks/relatorios";

interface Props {
  onClose: () => void;
}

const projects = [
  "CTA Urbano Campinas", "Inventário Parque das Acácias", "Inventário Zona Central",
  "Loteamento Green Park", "CTA Vinhedo", "CTA Zona Sul", "Inventário Guarulhos",
  "Carbono Urbano Municipal", "Gestão UC Mata Verde", "Compensação Rio Bonito",
];

const municipalities = ["São Paulo", "Campinas", "Jundiaí", "Vinhedo", "Guarulhos"];

export default function ReportGeneratorWizard({ onClose }: Props) {
  const [step, setStep] = useState(1);
  const [reportType, setReportType] = useState("");
  const [scope, setScope] = useState({ project: "", municipality: "", district: "", area: "", period: "", ctaCode: "", responsible: "" });
  const [filters, setFilters] = useState({ health: "", risk: "", species: "", management: "", hasPhoto: "", hasInspection: "", hasDoc: "", hasRRR: "" });
  const [sections, setSections] = useState<string[]>(["capa", "sumario", "indicadores", "tabelas", "graficos"]);
  const [format, setFormat] = useState("PDF");

  const toggleSection = (id: string) => {
    setSections((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const selectedType = generatorReportTypes.find((t) => t.id === reportType);

  const handleGenerate = () => {
    // Simulate report generation
    console.log("Gerando relatório:", { reportType, scope, filters, sections, format });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-background-200 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-foreground-900 font-heading">Gerador de Relatórios</h2>
            <p className="text-xs text-foreground-500 mt-0.5">Etapa {step} de 6</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center">
            <i className="ri-close-line text-foreground-500 w-full h-full flex items-center justify-center text-lg" />
          </button>
        </div>

        {/* Step progress */}
        <div className="px-6 py-3 border-b border-background-100 shrink-0">
          <div className="flex items-center gap-2">
            {["Tipo", "Escopo", "Filtros", "Conteúdo", "Formato", "Revisar"].map((label, i) => (
              <div key={i} className="flex items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  step > i + 1 ? "bg-primary-500 text-white" : step === i + 1 ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-400"
                }`}>
                  {step > i + 1 ? <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" /> : i + 1}
                </div>
                <span className={`text-xs font-medium truncate ${step >= i + 1 ? "text-foreground-700" : "text-foreground-400"}`}>{label}</span>
                {i < 5 && <div className={`flex-1 h-0.5 rounded ${step > i + 1 ? "bg-primary-300" : "bg-background-200"}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Step 1 — Type */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-foreground-600 mb-3">Selecione o tipo de relatório que deseja gerar:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {generatorReportTypes.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setReportType(t.id)}
                    className={`text-left p-3 rounded-xl border transition-colors ${
                      reportType === t.id
                        ? "border-primary-400 bg-primary-50 ring-1 ring-primary-200"
                        : "border-background-200 hover:border-primary-200 hover:bg-background-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${reportType === t.id ? "bg-primary-100" : "bg-background-100"}`}>
                        <i className={`${t.icon} ${reportType === t.id ? "text-primary-600" : "text-foreground-500"} w-full h-full flex items-center justify-center text-base`} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-400 mt-0.5">{t.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Scope */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-600">Defina o escopo do relatório:</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Projeto</label>
                  <select value={scope.project} onChange={(e) => setScope({ ...scope, project: e.target.value })} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 cursor-pointer">
                    <option value="">Selecione...</option>
                    {projects.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Município</label>
                  <select value={scope.municipality} onChange={(e) => setScope({ ...scope, municipality: e.target.value })} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 cursor-pointer">
                    <option value="">Selecione...</option>
                    {municipalities.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Bairro</label>
                  <input type="text" value={scope.district} onChange={(e) => setScope({ ...scope, district: e.target.value })} placeholder="Opcional" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Área Verde / UC</label>
                  <input type="text" value={scope.area} onChange={(e) => setScope({ ...scope, area: e.target.value })} placeholder="Opcional" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Período</label>
                  <input type="text" value={scope.period} onChange={(e) => setScope({ ...scope, period: e.target.value })} placeholder="Ex: Jan/2026 – Jun/2026" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Código CTA específico</label>
                  <input type="text" value={scope.ctaCode} onChange={(e) => setScope({ ...scope, ctaCode: e.target.value })} placeholder="Opcional" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Filters */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-600">Aplique filtros nos dados do relatório:</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: "health", label: "Estado fitossanitário", opts: ["", "Bom", "Regular", "Ruim", "Morta"], val: filters.health },
                  { id: "risk", label: "Classe de risco", opts: ["", "Baixo", "Médio", "Alto", "Crítico"], val: filters.risk },
                  { id: "species", label: "Espécie", opts: [], val: filters.species, isInput: true },
                  { id: "management", label: "Recomendação de manejo", opts: ["", "Poda", "Tratamento", "Remoção", "Replantio"], val: filters.management },
                ].map((f) => (
                  <div key={f.id}>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">{f.label}</label>
                    {"isInput" in f ? (
                      <input type="text" placeholder="Digite a espécie..." className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 focus:outline-none focus:border-primary-300" />
                    ) : (
                      <select value={f.val} onChange={(e) => setFilters({ ...filters, [f.id]: e.target.value })} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-600 cursor-pointer">
                        {f.opts.map((o) => <option key={o} value={o}>{o || "Todos"}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "hasPhoto", label: "Com foto" },
                  { id: "hasInspection", label: "Com vistoria" },
                  { id: "hasDoc", label: "Com documento" },
                  { id: "hasRRR", label: "Com RRR" },
                ].map((cb) => (
                  <label key={cb.id} className="flex items-center gap-2 text-sm text-foreground-600 cursor-pointer">
                    <input type="checkbox" className="rounded border-background-300 text-primary-500" />
                    {cb.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 4 — Content */}
          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-foreground-600 mb-3">Selecione as seções do relatório:</p>
              <div className="grid grid-cols-2 gap-2">
                {contentSections.map((s) => (
                  <label key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    sections.includes(s.id) ? "border-primary-400 bg-primary-50" : "border-background-200 hover:border-primary-200"
                  }`}>
                    <input
                      type="checkbox"
                      checked={sections.includes(s.id)}
                      onChange={() => toggleSection(s.id)}
                      className="rounded border-background-300 text-primary-500"
                    />
                    <span className="text-sm text-foreground-700">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 5 — Format */}
          {step === 5 && (
            <div className="space-y-3">
              <p className="text-sm text-foreground-600 mb-3">Escolha o formato de saída:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {outputFormats.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    className={`p-4 rounded-xl border text-center transition-colors ${
                      format === f ? "border-primary-400 bg-primary-50 ring-1 ring-primary-200" : "border-background-200 hover:border-primary-200 hover:bg-background-50"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center ${format === f ? "bg-primary-100" : "bg-background-100"}`}>
                      <i className={`${f === "PDF" ? "ri-file-pdf-line" : f === "XLSX" || f === "CSV" ? "ri-file-excel-line" : f === "DOCX" ? "ri-file-word-line" : "ri-map-2-line"} ${format === f ? "text-primary-500" : "text-foreground-500"} w-full h-full flex items-center justify-center text-lg`} />
                    </div>
                    <div className="text-sm font-semibold text-foreground-900">{f}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 6 — Review */}
          {step === 6 && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-600">Revise os parâmetros antes de gerar:</p>
              <div className="bg-background-50 rounded-xl p-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-foreground-500">Tipo:</span><span className="font-medium text-foreground-900">{selectedType?.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Projeto:</span><span className="font-medium text-foreground-900">{scope.project || "—"}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Município:</span><span className="font-medium text-foreground-900">{scope.municipality || "—"}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Formato:</span><span className="font-medium text-foreground-900">{format}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Seções:</span><span className="font-medium text-foreground-900">{sections.length} seções selecionadas</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Registros estimados:</span><span className="font-medium text-foreground-900">~847 árvores</span></div>
              </div>
              <div className="bg-accent-50 rounded-xl border border-accent-200 p-4">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-accent-600 w-5 h-5 flex items-center justify-center shrink-0" />
                  <p className="text-xs text-accent-700">O relatório será gerado com os dados atuais do sistema. O tempo de geração depende do volume de dados e das seções selecionadas. Relatórios grandes podem levar alguns minutos.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-background-200 px-6 py-4 flex items-center justify-between shrink-0">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            {step === 1 ? "Cancelar" : "Voltar"}
          </button>
          {step < 6 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !reportType}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              Próximo
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              className="px-6 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-magic-line w-4 h-4 inline-flex items-center justify-center mr-1" />
              Gerar relatório
            </button>
          )}
        </div>
      </div>
    </div>
  );
}